import { ChevronDown } from 'lucide-react';
import { EventStatus, isAdmin } from '@peles/shared';
import { StatusBadge } from '../common/EntityList';
import { DropdownMenu } from '../common/DropdownMenu';
import { useUpdateEvent } from '../../hooks/useEvents';
import { useDialog } from '../../context/DialogContext';
import { useAuth } from '../../context/AuthContext';
import { EVENT_STATUS_OPTIONS, eventStatusColor, eventStatusLabel } from './eventStatus';

/** An event's status as a coloured pill. */
export function EventStatusBadge({ status }: { status: string }) {
  return <StatusBadge label={eventStatusLabel(status)} color={eventStatusColor(status)} />;
}

const PILL_CLASSES: Record<string, string> = {
  red: 'bg-red-100 text-red-700',
  green: 'bg-green-100 text-green-700',
  blue: 'bg-blue-100 text-blue-700',
  yellow: 'bg-yellow-100 text-yellow-700',
  purple: 'bg-purple-100 text-purple-700',
  gray: 'bg-gray-100 text-gray-700',
};

/**
 * The status pill in the event header, which changes the status in place.
 * Cancelling asks first — it is the one choice that takes an event off every
 * default view — and is offered to administrators only, as the server allows.
 */
export function EventStatusMenu({ eventId, status }: { eventId: string; status: string }) {
  const updateEvent = useUpdateEvent();
  const { confirm, alert } = useDialog();
  const { user } = useAuth();
  const canCancel = !!user && isAdmin(user.systemRole);
  const options = EVENT_STATUS_OPTIONS.filter(
    (s) => s.id !== EventStatus.CANCELLED || canCancel || status === EventStatus.CANCELLED,
  );

  const choose = async (next: EventStatus) => {
    if (next === status) return;
    if (next === EventStatus.CANCELLED) {
      const ok = await confirm({
        title: 'Cancel this event?',
        message: 'It will be hidden from the default events list and dashboard.',
        confirmLabel: 'Cancel event',
        cancelLabel: 'Keep it',
        variant: 'danger',
      });
      if (!ok) return;
    }
    updateEvent.mutate(
      { id: eventId, updates: { status: next } },
      {
        onError: () => {
          void alert({
            title: 'Status not changed',
            message: 'Please try again.',
            variant: 'error',
          });
        },
      },
    );
  };

  return (
    <DropdownMenu
      ariaLabel="Change status"
      menuClassName="w-48"
      items={options.map((s) => ({
        id: s.id,
        label: <StatusBadge label={s.name} color={eventStatusColor(s.id)} />,
        selected: s.id === status,
        onSelect: () => void choose(s.id),
      }))}
      trigger={(props) => (
        <button
          type="button"
          {...props}
          aria-label={`Status: ${eventStatusLabel(status)}. Change status`}
          disabled={updateEvent.isPending}
          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold transition-shadow hover:ring-2 hover:ring-offset-1 hover:ring-gray-200 disabled:opacity-60 ${
            PILL_CLASSES[eventStatusColor(status)]
          }`}
        >
          {eventStatusLabel(status)}
          <ChevronDown size={12} />
        </button>
      )}
    />
  );
}
